import React from "react";
import { Checkbox, Label, Radio, RadioGroup, Slider } from "@fluentui/react-components";
import type { ChartTypeOption } from "../visualConfig";
import { getCapabilities } from "../visualConfig";
import { type ChartStyleSectionProps, useSliderStyles } from "./shared";

interface BarStyleProps extends ChartStyleSectionProps {
  chartType: ChartTypeOption;
}

const BarStyle: React.FC<BarStyleProps> = ({ chartStyle: cs, onStyleChange, chartType }) => {
  const styles = useSliderStyles();
  const caps = getCapabilities(chartType);
  const isAutoWidth = cs.barWidth == null;

  return (
    <>
      {/* ── Stacking ─────────────────────────────────────────── */}
      {caps.stacking && (
        <div className={styles.sliderField}>
          <Label size="small">Stacking</Label>
          <RadioGroup
            layout="horizontal"
            value={cs.stacking ?? 'off'}
            onChange={(_, data) => onStyleChange({ stacking: (data.value as 'off' | 'normal' | 'percent') ?? 'off' })}
          >
            <Radio value="off" label="None" />
            <Radio value="normal" label="Stacked" />
            <Radio value="percent" label="100% Stacked" />
          </RadioGroup>
        </div>
      )}

      {/* ── Horizontal bars ──────────────────────────────────── */}
      {caps.horizontal && (
        <Checkbox
          label="Horizontal bars"
          checked={cs.horizontal ?? false}
          onChange={(_, data) => onStyleChange({ horizontal: !!data.checked })}
        />
      )}

      {/* ── Bar Width ────────────────────────────────────────── */}
      <div className={styles.sliderField}>
        <Label size="small">Bar Width</Label>
        <Checkbox
          label="Auto"
          checked={isAutoWidth}
          onChange={(_, data) =>
            onStyleChange({ barWidth: data.checked ? undefined : 50 })
          }
          style={{ marginBottom: 4 }}
        />
        {!isAutoWidth && (
          <div className={styles.sliderRow}>
            <Slider
              min={10} max={100} step={5}
              value={cs.barWidth!}
              onChange={(_, data) => onStyleChange({ barWidth: data.value })}
              style={{ flex: 1 }}
            />
            <span className={styles.sliderValue}>{cs.barWidth}%</span>
          </div>
        )}
      </div>

      {/* ── Corner Radius ────────────────────────────────────── */}
      <div className={styles.sliderField}>
        <Label size="small">Corner Radius</Label>
        <div className={styles.sliderRow}>
          <Slider
            min={0} max={10} step={1}
            value={cs.borderRadius ?? 0}
            onChange={(_, data) => onStyleChange({ borderRadius: data.value })}
            style={{ flex: 1 }}
          />
          <span className={styles.sliderValue}>{cs.borderRadius ?? 0}px</span>
        </div>
      </div>
    </>
  );
};

export default BarStyle;